import React, { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { client, fetchParentChildRelationships } from '../api/client'
import { NavigationBar } from '../components/NavigationBar'
import { useAuthStore } from '../stores/auth'

interface EditChildFormData {
  first_name: string
  last_name: string
  social_security_number: string
  gender: string
  birth_date: string
  family_status: string
  parent_type: string
  co_parent_type?: string
  guardians: string[]
  social_security_fund_owner?: number
}

// Map to convert display values to form values
const parentTypeMap: Record<string, string> = {
  'Father': 'father',
  'Mother': 'mother',
  'Parent 1': 'parent_one',
  'Parent 2': 'parent_two',
  'Πατέρας': 'father',  // Greek translations
  'Μητέρα': 'mother',
  'Γονέας 1': 'parent_one',
  'Γονέας 2': 'parent_two'
}

export function EditChildPage() {
  const { t } = useTranslation('children')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { parent } = useAuthStore()
  const [isLoading, setIsLoading] = useState(false)
  const [isDataLoading, setIsDataLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [registrationNumber, setRegistrationNumber] = useState<string>('')
  const [relationshipId, setRelationshipId] = useState<number | null>(null)
  const [originalParentType, setOriginalParentType] = useState<string>('')
  const [coParentRelationshipId, setCoParentRelationshipId] = useState<number | null>(null)
  const [originalCoParentType, setOriginalCoParentType] = useState<string>('')
  const [hasCoParent, setHasCoParent] = useState(false)
  const [socialSecurityFundOwner, setSocialSecurityFundOwner] = useState<number | null>(null)
  const [selectedGuardians, setSelectedGuardians] = useState<string[]>([])
  const [childGuardians, setChildGuardians] = useState<number[]>([])
  const [skipSsnChecks, setSkipSsnChecks] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError: setFieldError,
    reset,
    watch,
    setValue,
    clearErrors,
    getValues
  } = useForm<EditChildFormData>({
    defaultValues: {
      parent_type: '',
      co_parent_type: '',
      guardians: []
    }
  })

  // Get the current parent_type value from the form
  const parentTypeValue = watch('parent_type')
  const coParentTypeValue = watch('co_parent_type')
  const guardians = watch('guardians')

  // Use a ref to track the previous parent type values to prevent infinite loops
  const prevParentTypeRef = useRef(parentTypeValue);
  const prevCoParentTypeRef = useRef(coParentTypeValue);

  // Custom function to check for duplicate parent types (instead of using useEffect)
  useEffect(() => {
    // Only run this logic if both values have changed from their previous values
    // This prevents unnecessary validation cycles
    const parentTypeChanged = parentTypeValue !== prevParentTypeRef.current;
    const coParentTypeChanged = coParentTypeValue !== prevCoParentTypeRef.current;
    
    if ((parentTypeChanged || coParentTypeChanged) && hasCoParent) {
      // Update refs with current values
      prevParentTypeRef.current = parentTypeValue;
      prevCoParentTypeRef.current = coParentTypeValue;
      
      // Check for duplicate parent types
      if (parentTypeValue && coParentTypeValue && parentTypeValue === coParentTypeValue) {
        setFieldError('co_parent_type', {
          type: 'manual',
          message: t('form.validation.duplicateParentType')
        });
      } else if (coParentTypeValue && parentTypeValue) {
        // Only clear the error if both values exist and are different
        clearErrors('co_parent_type');
      }
    }
  }, [parentTypeValue, coParentTypeValue, hasCoParent, setFieldError, clearErrors, t]);

  // Set initial values when originalParentType or originalCoParentType change
  // This should only happen once when the component mounts
  useEffect(() => {
    if (originalParentType && !parentTypeValue) {
      setValue('parent_type', originalParentType);
    }
    
    if (originalCoParentType && !coParentTypeValue) {
      setValue('co_parent_type', originalCoParentType);
    }
  }, [originalParentType, originalCoParentType, setValue, parentTypeValue, coParentTypeValue]);

  // Check if the parent has a co-parent
  useEffect(() => {
    if (parent?.co_parent) {
      setHasCoParent(true)
    }
    
    // Initialize guardians with the primary parent if no guardians are set and we're not loading data
    if (parent?.id && !isDataLoading) {
      setSocialSecurityFundOwner(parent.id)
    }
  }, [parent, isDataLoading])

  // Handle guardian selection changes
  const handleGuardianChange = (value: string) => {
    // Get current guardians from the form
    const currentGuardians = guardians || [];
    let newGuardians: string[] = [...currentGuardians];
    
    if (newGuardians.includes(value)) {
      // If already selected, remove it
      newGuardians = newGuardians.filter(g => g !== value);
    } else {
      // Otherwise add it
      newGuardians.push(value);
    }
    
    // Update the form value directly
    setValue('guardians', newGuardians);
  };

  // Fetch child data on component mount
  useEffect(() => {
    const fetchChildData = async () => {
      try {
        setIsDataLoading(true)
        
        // Get the child data
        const childResponse = await client.get(`/children/${id}/`)
        const childData = childResponse.data
        
        // Initialize parent type variables
        let parentTypeValue = '';
        let coParentTypeValue = '';
        
        // Set registration number separately for display
        setRegistrationNumber(childData.registration_number)
        
        // Set skip_ssn_checks value
        setSkipSsnChecks(childData.skip_ssn_checks || false)
        
        // Set social security fund owner
        if (childData.social_security_fund_owner) {
          setSocialSecurityFundOwner(childData.social_security_fund_owner)
        }
        
        // Set guardians if available
        const guardianStrings: string[] = [];
        if (childData.guardians && Array.isArray(childData.guardians)) {
          setChildGuardians(childData.guardians);
          
          // Convert guardian IDs to strings for the form
          if (parent?.id && childData.guardians.includes(parent.id)) {
            guardianStrings.push('parent');
          }
          if (parent?.co_parent && childData.guardians.includes(parent.co_parent.id)) {
            guardianStrings.push('coParent');
          }
        }
        
        // Set the selected guardians state and form value
        setSelectedGuardians(guardianStrings);
        setValue('guardians', guardianStrings);
        
        // Check if we have co_parent_info with parent_type
        if (childData.co_parent_info && childData.co_parent_info.parent_type) {
          coParentTypeValue = childData.co_parent_info.parent_type;
          setOriginalCoParentType(coParentTypeValue);
        }
        
        // Format the date as YYYY-MM-DD for the date input
        const formattedDate = childData.birth_date.split('T')[0]
        
        try {
          // Get all parent-child relationships
          const relationships = await fetchParentChildRelationships()
          
          // First, find all relationships for this child
          const childRelationships = relationships.filter(
            (rel) => rel.child === parseInt(id as string)
          );
          
          // Check if we have parent IDs in the relationships
          const hasParentIds = childRelationships.some(rel => rel.parent !== undefined);
          
          if (!hasParentIds) {
            // If we don't have parent IDs, assume the first relationship is for the main parent
            // and the second relationship (if it exists) is for the co-parent
            if (childRelationships.length > 0) {
              parentTypeValue = childRelationships[0].parent_type;
              setOriginalParentType(parentTypeValue);
              
              // If there's a second relationship and we have a co-parent, use it for the co-parent
              if (childRelationships.length > 1 && parent?.co_parent) {
                coParentTypeValue = childRelationships[1].parent_type;
                setOriginalCoParentType(coParentTypeValue);
              }
            }
          } else {
            // Find the relationship for this child and the main parent
            const relationship = childRelationships.find(
              (rel) => rel.parent === parent?.id
            );
            
            // Find the relationship for this child and the co-parent
            const coParentRelationship = parent?.co_parent ? childRelationships.find(
              (rel) => rel.parent === parent.co_parent?.id
            ) : undefined;
            
            // Set parent types based on existing relationships
            if (relationship) {
              // Make sure we have a valid relationship ID
              if (relationship.id) {
                setRelationshipId(relationship.id);
              }
              
              // Set the parent type for the main parent from the API
              parentTypeValue = relationship.parent_type;
              setOriginalParentType(parentTypeValue);
            }
            
            // Set co-parent type based on existing relationships
            if (coParentRelationship) {
              if (coParentRelationship.id) {
                setCoParentRelationshipId(coParentRelationship.id);
              }
              
              // Set the parent type for the co-parent from the API
              coParentTypeValue = coParentRelationship.parent_type;
              setOriginalCoParentType(coParentTypeValue);
            }
          }
        } catch (error) {
          console.error('Error fetching relationships:', error);
        }
        
        // Reset form with fetched data
        reset({
          first_name: childData.first_name,
          last_name: childData.last_name,
          social_security_number: childData.social_security_number,
          gender: childData.gender,
          birth_date: formattedDate,
          family_status: childData.family_status,
          parent_type: parentTypeValue || '',
          co_parent_type: coParentTypeValue || '',
          guardians: guardianStrings,
          social_security_fund_owner: childData.social_security_fund_owner,
        }, {
          keepDirtyValues: false, // Overwrite any user-modified values
          keepErrors: false,      // Clear any validation errors
          keepIsValid: false,     // Reset validation state
          keepTouched: false,     // Reset touched fields
          keepIsSubmitted: false, // Reset submission state
          keepDirty: false        // Reset dirty state
        });

      } catch (error) {
        setError(t('errors.loadFailed'))
      } finally {
        setIsDataLoading(false)
      }
    }

    if (id) {
      fetchChildData()
    }
  }, [id, reset, t, parent?.id, parent?.co_parent, setValue])

  const onSubmit = async (data: EditChildFormData) => {
    setIsLoading(true)
    setError(null)

    // Validate that at least one guardian is selected
    if (!data.guardians || data.guardians.length === 0) {
      setFieldError('guardians', {
        type: 'manual',
        message: t('form.validation.guardiansRequired')
      });
      setIsLoading(false);
      return;
    }

    // Validate that co_parent_type is provided if the user has a co-parent
    if (hasCoParent && parent?.co_parent && (!data.co_parent_type || data.co_parent_type === '')) {
      setFieldError('co_parent_type', {
        type: 'manual',
        message: t('form.validation.coParentTypeRequired')
      });
      setIsLoading(false);
      return;
    }

    // Handle error from the backend without skip_ssn_checks enabled
    if (!skipSsnChecks) {
      // Get the birth date DDMMYY from YYYY-MM-DD
      const birthDateParts = data.birth_date.split('-');
      if (birthDateParts.length === 3) {
        const birthYear = birthDateParts[0].slice(-2); // Get last 2 digits
        const birthMonth = birthDateParts[1];
        const birthDay = birthDateParts[2];
        const birthDateDDMMYY = `${birthDay}${birthMonth}${birthYear}`;
        
        // Check if SSN starts with birth date
        if (data.social_security_number && data.social_security_number.length >= 6) {
          const ssnPrefix = data.social_security_number.substring(0, 6);
          if (ssnPrefix !== birthDateDDMMYY) {
            setFieldError('social_security_number', {
              type: 'manual',
              message: t('form.validation.ssnBirthDateMismatch')
            });
            setIsLoading(false);
            return;
          }
        }
      }
    }
    
    try {
      // Convert guardian strings to parent IDs
      const guardianIds: number[] = [];
      if (data.guardians.includes('parent') && parent?.id) {
        guardianIds.push(parent.id);
      }
      if (data.guardians.includes('coParent') && parent?.co_parent?.id) {
        guardianIds.push(parent.co_parent.id);
      }

      // Create the base child data object
      const childData: any = {
        first_name: data.first_name,
        last_name: data.last_name,
        social_security_number: data.social_security_number,
        gender: data.gender,
        birth_date: data.birth_date,
        family_status: data.family_status,
        parent_type: data.parent_type,
        guardians: guardianIds,
        social_security_fund_owner: data.social_security_fund_owner || parent?.id,
      };
      
      // Only include co_parent_type if there's a co-parent
      if (hasCoParent && parent?.co_parent) {
        childData.co_parent_type = data.co_parent_type;
      }
      
      // Update the child with all data
      const response = await client.put(`/children/${id}/`, childData);
      
      // Navigate back to the home page
      navigate('/');
    } catch (error: any) {
      // Error handling for child update
      
      if (error.response?.data) {
        // Handle field-specific errors
        Object.entries(error.response.data).forEach(([field, messages]) => {
          if (field as keyof EditChildFormData) {
            // Check if this is the AMKA date format error message
            let errorMessage = Array.isArray(messages) ? messages[0] : messages;
            
            // Translate specific backend error messages
            if (typeof errorMessage === 'string') {
              if (errorMessage.includes('The first 6 digits of AMKA must represent a valid date in DDMMYY format')) {
                errorMessage = t('form.validation.ssnDateFormat');
              } else if (errorMessage.includes('The first 6 digits of AMKA must match the birth date')) {
                errorMessage = t('form.validation.ssnBirthDateMismatch');
              } else if (errorMessage.includes('Child with this Social Security Number already exists')) {
                errorMessage = t('form.validation.ssnExists');
              } else if (errorMessage.includes('This parent type is already assigned for this child')) {
                errorMessage = t('form.validation.parentTypeAlreadyUsed');
              } else if (errorMessage.includes('Social security fund owner must have a social security number')) {
                // Don't set the global error message since we're handling it with a custom element
                
                // Create a custom error handler that will show the message with a link
                const targetParentId = getValues('social_security_fund_owner');
                const handleSSNLinkClick = () => {
                  sessionStorage.setItem('scrollToSSN', 'true');
                  sessionStorage.setItem('highlightSSN', 'true');
                  sessionStorage.setItem('targetParentId', String(targetParentId));
                };
                
                // Override the error display in the form for this field
                const socialSecurityField = document.querySelector('[id="social_security_fund_owner"]')?.parentElement;
                if (socialSecurityField) {
                  const errorP = socialSecurityField.querySelector('p.text-neobrutalism-red') || document.createElement('p');
                  errorP.className = 'text-neobrutalism-red mt-1 font-bold';
                  const linkText = t('form.validation.ssnRequiredForFundOwner');
                  const [beforeHere, afterHere] = linkText.split('εδώ');
                  const [beforeHereEn, afterHereEn] = linkText.split('here');
                  
                  // Use the appropriate split based on the current language
                  const isGreek = beforeHere !== linkText;
                  if (isGreek) {
                    errorP.innerHTML = `${beforeHere}<a href="/profile/edit" class="text-blue-600 underline hover:text-blue-800" id="ssn-link">εδώ</a>${afterHere || ''}`;
                  } else {
                    errorP.innerHTML = `${beforeHereEn}<a href="/profile/edit" class="text-blue-600 underline hover:text-blue-800" id="ssn-link">here</a>${afterHereEn || ''}`;
                  }
                  
                  if (!socialSecurityField.querySelector('p.text-neobrutalism-red')) {
                    socialSecurityField.appendChild(errorP);
                  }
                  
                  document.getElementById('ssn-link')?.addEventListener('click', handleSSNLinkClick);
                }
                
                // Clear the errorMessage to prevent it from showing in global error section
                errorMessage = '';
              } else if (errorMessage.includes('This field may not be blank') && field === 'co_parent_type') {
                errorMessage = t('form.validation.coParentTypeRequired');
              }
            }
            
            setFieldError(field as keyof EditChildFormData, {
              type: 'manual',
              message: errorMessage,
            })
          } else {
            let errorMessage = Array.isArray(messages) ? messages[0] : messages;
            
            // Translate specific backend error messages
            if (typeof errorMessage === 'string') {
              if (errorMessage.includes('The first 6 digits of AMKA must represent a valid date in DDMMYY format')) {
                errorMessage = t('form.validation.ssnDateFormat');
              } else if (errorMessage.includes('The first 6 digits of AMKA must match the birth date')) {
                errorMessage = t('form.validation.ssnBirthDateMismatch');
              } else if (errorMessage.includes('Child with this Social Security Number already exists')) {
                errorMessage = t('form.validation.ssnExists');
              } else if (errorMessage.includes('This parent type is already assigned for this child')) {
                errorMessage = t('form.validation.parentTypeAlreadyUsed');
              } else if (errorMessage.includes('Social security fund owner must have a social security number')) {
                errorMessage = t('form.validation.ssnRequiredForFundOwner');
              } else if (errorMessage.includes('This field may not be blank') && field === 'co_parent_type') {
                errorMessage = t('form.validation.coParentTypeRequired');
              }
            }
            
            setError(errorMessage)
          }
        })
      } else {
        setError(t('errors.updateFailed'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="edit-child-container" style={{ minHeight: '100vh' }}>
      {/* Navigation Bar */}
      <NavigationBar />

      {/* Main Content */}
      <div className="max-w-2xl mx-auto py-12 px-4">
        <div className="card-neobrutalism">
          <div className="mb-6">
            <h1 className="text-2xl font-bold">{t('edit.title')}</h1>
          </div>

          {error && (
            <div className="bg-neobrutalism-red p-4 rounded-lg border-2 border-black mb-4">
              <p className="text-neobrutalism-text font-bold">{error}</p>
            </div>
          )}

          {isDataLoading ? (
            <div className="flex justify-center items-center h-40">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-neobrutalism-accent"></div>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Registration Number (Read-only) */}
              <div>
                <label className="block font-bold mb-2" htmlFor="registration_number">
                  {t('form.registrationNumber')}
                </label>
                <input
                  id="registration_number"
                  type="text"
                  value={registrationNumber}
                  disabled
                  className="w-full p-2 border-2 border-black bg-gray-100"
                />
              </div>
              
              {/* First Name and Last Name - Row 1 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-bold mb-2" htmlFor="first_name">
                    {t('form.firstName')}
                  </label>
                  <input
                    id="first_name"
                    {...register('first_name', { required: t('form.validation.firstNameRequired') })}
                    className="w-full p-2 border-2 border-black"
                  />
                  {errors.first_name && (
                    <p className="text-neobrutalism-red mt-1 font-bold">{errors.first_name.message}</p>
                  )}
                </div>
                <div>
                  <label className="block font-bold mb-2" htmlFor="last_name">
                    {t('form.lastName')}
                  </label>
                  <input
                    id="last_name"
                    {...register('last_name', { required: t('form.validation.lastNameRequired') })}
                    className="w-full p-2 border-2 border-black"
                  />
                  {errors.last_name && (
                    <p className="text-neobrutalism-red mt-1 font-bold">{errors.last_name.message}</p>
                  )}
                </div>
              </div>

              {/* Social Security Number and Birth Date - Row 2 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-bold mb-2" htmlFor="social_security_number">
                    {t('form.ssn')} <span className="text-sm font-normal">{t('form.childSuffix')}</span>
                  </label>
                  <input
                    id="social_security_number"
                    {...register('social_security_number', {
                      required: t('form.validation.ssnRequired'),
                      pattern: {
                        value: /^\d{11}$/,
                        message: t('form.validation.ssnFormat')
                      }
                    })}
                    className="w-full p-2 border-2 border-black"
                  />
                  {errors.social_security_number && (
                    <p className="text-neobrutalism-red mt-1 font-bold">{errors.social_security_number.message}</p>
                  )}
                </div>
                <div>
                  <label className="block font-bold mb-2" htmlFor="birth_date">
                    {t('form.birthDate')}
                  </label>
                  <input
                    id="birth_date"
                    type="date"
                    {...register('birth_date', { required: t('form.validation.birthDateRequired') })}
                    className="w-full p-2 border-2 border-black"
                  />
                  {errors.birth_date && (
                    <p className="text-neobrutalism-red mt-1 font-bold">{errors.birth_date.message}</p>
                  )}
                </div>
              </div>

              {/* Gender and Family Status - Row 3 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block font-bold mb-2" htmlFor="gender">
                    {t('form.gender')}
                  </label>
                  <select
                    id="gender"
                    {...register('gender', { required: t('form.validation.genderRequired') })}
                    className="w-full p-2 border-2 border-black"
                  >
                    <option value="">{t('form.gender')}</option>
                    <option value="male">{t('form.genderOptions.male')}</option>
                    <option value="female">{t('form.genderOptions.female')}</option>
                  </select>
                  {errors.gender && (
                    <p className="text-neobrutalism-red mt-1 font-bold">{errors.gender.message}</p>
                  )}
                </div>
                <div>
                  <label className="block font-bold mb-2" htmlFor="family_status">
                    {t('form.familyStatus')}
                  </label>
                  <select
                    id="family_status"
                    {...register('family_status', { required: t('form.validation.familyStatusRequired') })}
                    className="w-full p-2 border-2 border-black"
                  >
                    <option value="">{t('form.familyStatus')}</option>
                    <option value="married">{t('form.familyStatusOptions.married')}</option>
                    <option value="divorced">{t('form.familyStatusOptions.divorced')}</option>
                    <option value="separated">{t('form.familyStatusOptions.separated')}</option>
                    <option value="widowed_parent">{t('form.familyStatusOptions.widowed_parent')}</option>
                    <option value="single_parent">{t('form.familyStatusOptions.single_parent')}</option>
                    <option value="without_parents">{t('form.familyStatusOptions.without_parents')}</option>
                  </select>
                  {errors.family_status && (
                    <p className="text-neobrutalism-red mt-1 font-bold">{errors.family_status.message}</p>
                  )}
                </div>
              </div>

              {/* Parent Type and Co-Parent Type (if exists) - Row 4 */}
              {hasCoParent ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block font-bold mb-2" htmlFor="parent_type">
                      {t('form.parentType')}
                    </label>
                    <select
                      id="parent_type"
                      {...register('parent_type', { required: t('form.validation.parentTypeRequired') })}
                      className="w-full p-2 border-2 border-black"
                    >
                      <option value="">{t('form.parentType')}</option>
                      <option value="father">{t('form.parentTypeOptions.father')}</option>
                      <option value="mother">{t('form.parentTypeOptions.mother')}</option>
                      <option value="parent_one">{t('form.parentTypeOptions.parent_one')}</option>
                      <option value="parent_two">{t('form.parentTypeOptions.parent_two')}</option>
                      <option value="other">{t('form.parentTypeOptions.other')}</option>
                    </select>
                    {errors.parent_type && (
                      <p className="text-neobrutalism-red mt-1 font-bold">{errors.parent_type.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block font-bold mb-2" htmlFor="co_parent_type">
                      {t('form.coParentType')}
                    </label>
                    <select
                      id="co_parent_type"
                      {...register('co_parent_type', { required: t('form.validation.coParentTypeRequired') })}
                      className="w-full p-2 border-2 border-black"
                    >
                      <option value="">{t('form.coParentTypeNone')}</option>
                      <option value="father">{t('form.parentTypeOptions.father')}</option>
                      <option value="mother">{t('form.parentTypeOptions.mother')}</option>
                      <option value="parent_one">{t('form.parentTypeOptions.parent_one')}</option>
                      <option value="parent_two">{t('form.parentTypeOptions.parent_two')}</option>
                      <option value="other">{t('form.parentTypeOptions.other')}</option>
                    </select>
                    {errors.co_parent_type && (
                      <p className="text-neobrutalism-red mt-1 font-bold">{errors.co_parent_type.message}</p>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block font-bold mb-2" htmlFor="parent_type">
                    {t('form.parentType')}
                  </label>
                  <select
                    id="parent_type"
                    {...register('parent_type', { required: t('form.validation.parentTypeRequired') })}
                    className="w-full p-2 border-2 border-black"
                  >
                    <option value="">{t('form.parentType')}</option>
                    <option value="father">{t('form.parentTypeOptions.father')}</option>
                    <option value="mother">{t('form.parentTypeOptions.mother')}</option>
                    <option value="parent_one">{t('form.parentTypeOptions.parent_one')}</option>
                    <option value="parent_two">{t('form.parentTypeOptions.parent_two')}</option>
                    <option value="other">{t('form.parentTypeOptions.other')}</option>
                  </select>
                  {errors.parent_type && (
                    <p className="text-neobrutalism-red mt-1 font-bold">{errors.parent_type.message}</p>
                  )}
                </div>
              )}

              {/* Guardians - Row 4.5 */}
              <div>
                <label className="block font-bold mb-2">
                  {t('form.guardians')}
                </label>
                <p className="text-sm mb-2">{t('form.guardiansDescription')}</p>
                
                <div className="space-y-2">
                  {/* Main Parent Option */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="guardian_parent"
                      checked={guardians?.includes('parent')}
                      onChange={() => handleGuardianChange('parent')}
                      className="mr-2 h-5 w-5"
                    />
                    <label htmlFor="guardian_parent" className="cursor-pointer">
                      {parent?.first_name} {parent?.last_name}
                    </label>
                  </div>
                  
                  {/* Co-Parent Option (only if there is a co-parent) */}
                  {hasCoParent && parent?.co_parent && (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="guardian_coparent"
                        checked={guardians?.includes('coParent')}
                        onChange={() => handleGuardianChange('coParent')}
                        className="mr-2 h-5 w-5"
                      />
                      <label htmlFor="guardian_coparent" className="cursor-pointer">
                        {parent.co_parent.first_name} {parent.co_parent.last_name}
                      </label>
                    </div>
                  )}
                </div>
                
                {errors.guardians && (
                  <p className="text-neobrutalism-red mt-1 font-bold">{errors.guardians.message}</p>
                )}
              </div>

              {/* Social Security Fund Owner - Row 5 */}
              <div>
                <label className="block font-bold mb-2" htmlFor="social_security_fund_owner">
                  {t('form.socialSecurityFundOwner')}
                </label>
                <select
                  id="social_security_fund_owner"
                  {...register('social_security_fund_owner')}
                  className="w-full p-2 border-2 border-black"
                  defaultValue={socialSecurityFundOwner || parent?.id}
                >
                  <option value={parent?.id}>{parent?.first_name} {parent?.last_name}</option>
                  {hasCoParent && parent?.co_parent && (
                    <option value={parent.co_parent.id}>{parent.co_parent.first_name} {parent.co_parent.last_name}</option>
                  )}
                </select>
                {errors.social_security_fund_owner && (
                  <p className="text-neobrutalism-red mt-1 font-bold">{errors.social_security_fund_owner.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Link
                  to="/"
                  className="btn-neobrutalism bg-neobrutalism-red text-center text-xs sm:text-base py-2 sm:py-3 px-2 sm:px-4"
                >
                  {t('form.cancel')}
                </Link>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-neobrutalism bg-neobrutalism-accent text-xs sm:text-base py-2 sm:py-3 px-2 sm:px-4"
                >
                  {isLoading ? t('form.saving') : t('form.save')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
} 